import { Loader2, Lock, Save } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * A moldura das seções do RDO — título, conteúdo, e o botão que grava aquela seção.
 *
 * ## Por que cada seção grava sozinha
 *
 * São cinco escritas diferentes no servidor — clima, tarefa, observação, efetivo,
 * equipamento —, cada uma com a sua regra e a sua recusa. Um "salvar tudo" teria de
 * decidir o que fazer quando a terceira falhasse com as duas primeiras já gravadas, e a
 * resposta honesta a essa pergunta é exatamente o que este desenho evita precisar:
 * grava-se o que se acabou de digitar, e a recusa aparece embaixo do campo que a causou.
 *
 * Em troca, a pessoa toca em gravar mais de uma vez por dia. É o preço, e ele é pequeno
 * perto de descobrir que o efetivo não entrou porque o equipamento tinha uma linha
 * inválida.
 *
 * ## O documento emitido não tem botão
 *
 * `isEditable` recusa toda escrita em documento emitido (RN-210), e o caso de uso é quem
 * decide. Aqui a recusa é só antecipada: em vez de oferecer um botão que vai falhar,
 * a seção diz por que não há botão e o que fazer — abrir revisão, que mora no cabeçalho.
 */
export const SELECT_CLASS =
  'h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

export function ReportSection({
  title,
  description,
  editable,
  isSaving,
  errorMessage,
  onSave,
  actions,
  children,
}: {
  title: string;
  description?: string;
  /** Falso quando o documento está emitido: a seção vira leitura, com o motivo à vista. */
  editable: boolean;
  isSaving: boolean;
  errorMessage?: string | undefined;
  /** Ausente quando a seção não grava nada — as frentes do dia são derivadas. */
  onSave?: (() => void) | undefined;
  /** Botões da seção que não são o de gravar — acrescentar linha, por exemplo. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const id = `rdo-${title.toLowerCase().replaceAll(/[^a-z0-9]+/gu, '-')}`;

  return (
    <Card aria-labelledby={id} role="region">
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 id={id} className="text-base font-semibold">
            {title}
          </h3>
          {actions !== undefined && editable && <div className="flex gap-2">{actions}</div>}
        </div>

        {description !== undefined && (
          <p className="-mt-2 text-sm text-(--color-content-muted)">{description}</p>
        )}

        {children}

        {errorMessage !== undefined && (
          <p role="alert" className="text-sm text-(--color-state-danger)">
            {errorMessage}
          </p>
        )}

        {onSave !== undefined &&
          (editable ? (
            <div className="flex justify-end">
              <Button variant="secondary" disabled={isSaving} onClick={onSave}>
                {isSaving ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Gravar
              </Button>
            </div>
          ) : (
            <p className="flex items-center gap-2 text-xs text-(--color-content-muted)">
              <Lock className="size-3.5" aria-hidden="true" />
              Documento emitido. Abra revisão no cabeçalho para alterar.
            </p>
          ))}
      </CardContent>
    </Card>
  );
}
